import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Lightbulb, Bug, Paperclip, X, Send } from 'lucide-react';
import { getAccessToken } from '../services/auth';

// The Wish list, on the Team page beside Everything and Mine.
//
// ⚠️ IT IS A SUGGESTION BOX, NOT A BUILD QUEUE. Greg considered wiring it into Claude Code and
// decided against it: "report a bug, maybe they don't need the handoff for Claude Code. Don't
// do that. Just give them a form." A row here is somebody's idea; it becomes work when it is
// promoted to a team project, and not before.
//
// ⚠️ SCREENSHOTS GO BROWSER TO R2 ON A SIGNED PUT, never as base64 through the function - a
// Netlify request body caps around 6MB and base64 inflates a phone screenshot by a third, so
// the obvious shape fails on exactly the big picture somebody needs to send.
//
// ⚠️ The stored value is the R2 KEY. A signed read URL lasts an hour and a wish outlives it,
// so a fresh URL is minted every time one is opened.

const base = () => (window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions'
  : '/.netlify/functions');

async function post(fn, payload) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(base() + '/' + fn, { method: 'POST', headers, body: JSON.stringify(payload) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || fn + ' ' + res.status);
  return data;
}

const KIND = {
  idea: { label: 'Idea', icon: Lightbulb, color: 'var(--navy)' },
  bug: { label: 'Something is broken', icon: Bug, color: 'var(--orange)' },
};

const STATUS = {
  new: { label: 'New', bg: '#EDF1F4', fg: '#3E5A6B' },
  queued: { label: 'Queued', bg: '#E4EEF6', fg: '#00395C' },
  building: { label: 'Building', bg: '#FDE9DF', fg: '#B8420F' },
  done: { label: 'Done', bg: '#E4F1E8', fg: '#1E6B39' },
  parked: { label: 'Parked', bg: '#F1F1F1', fg: '#6B6B6B' },
};

// Upload one file straight to R2 and hand back its key. Returns null on failure rather than
// throwing - a screenshot that will not go up must never cost somebody the words they typed.
async function uploadOne(file) {
  try {
    const sign = await post('r2-upload', {
      action: 'presign-doc', filename: file.name, contentType: file.type, prefix: 'wish-list',
    });
    const put = await fetch(sign.uploadUrl, {
      method: 'PUT', headers: { 'Content-Type': sign.contentType }, body: file,
    });
    if (!put.ok) return null;
    return { key: sign.key, filename: file.name, contentType: sign.contentType };
  } catch (_) { return null; }
}

function Attachment({ file }) {
  const [url, setUrl] = useState(null);
  const isImage = /^image\//.test(file.contentType || '');
  const open = async () => {
    try {
      const r = await post('r2-upload', { action: 'presign-read', key: file.key });
      setUrl(r.url);
      window.open(r.url, '_blank', 'noopener');
    } catch (_) { /* the link simply does not open; the wish still reads fine */ }
  };
  return (
    <button onClick={open} title={file.filename || 'Attachment'}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, minHeight: 32, padding: '4px 10px',
        borderRadius: 8, border: '1px solid var(--line, #D9E2E8)', background: 'var(--surface, #fff)',
        cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', color: 'var(--navy)', maxWidth: 220,
      }}>
      <Paperclip size={12} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {isImage ? 'Screenshot' : (file.filename || 'File')}{url ? '' : ''}
      </span>
    </button>
  );
}

function Composer({ onSaved }) {
  const [kind, setKind] = useState('idea');
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [page, setPage] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const pick = useRef(null);

  const addFiles = async (list) => {
    const chosen = Array.from(list || []).slice(0, 8 - files.length);
    if (!chosen.length) return;
    setBusy(true);
    const up = [];
    for (const f of chosen) {
      const r = await uploadOne(f);
      if (r) up.push(r); else setErr('"' + f.name + '" would not upload. The rest is fine.');
    }
    setFiles(prev => [...prev, ...up]);
    setBusy(false);
  };

  // Paste a screenshot straight in - it is how most people take one, and asking them to save
  // it to disk first is where a suggestion quietly stops being worth the effort.
  const onPaste = (e) => {
    const imgs = Array.from(e.clipboardData?.items || [])
      .filter(i => i.kind === 'file' && /^image\//.test(i.type))
      .map(i => i.getAsFile())
      .filter(Boolean);
    if (imgs.length) { e.preventDefault(); addFiles(imgs); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true); setErr(null);
    try {
      await post('wish-list', { action: 'add', kind, title, detail, page, files });
      setTitle(''); setDetail(''); setPage(''); setFiles([]); setKind('idea');
      onSaved();
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  };

  const input = {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14,
    border: '1px solid var(--line, #D9E2E8)', borderRadius: 8, fontFamily: 'inherit',
  };

  return (
    <form onSubmit={submit} onPaste={onPaste}
      style={{ background: 'var(--surface, #fff)', border: '1px solid var(--line, #D9E2E8)', borderRadius: 14, padding: 20, marginBottom: 26 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {Object.entries(KIND).map(([key, k]) => {
          const Icon = k.icon;
          const on = kind === key;
          return (
            <button type="button" key={key} onClick={() => setKind(key)} style={{
              display: 'flex', alignItems: 'center', gap: 7, minHeight: 40, padding: '0 15px',
              borderRadius: 999, cursor: 'pointer', fontSize: 13.5, fontWeight: on ? 700 : 600, fontFamily: 'inherit',
              border: '1px solid ' + (on ? k.color : 'var(--line, #D9E2E8)'),
              background: on ? k.color : 'transparent', color: on ? '#fff' : 'var(--muted, #59707E)',
            }}><Icon size={15} /> {k.label}</button>
          );
        })}
      </div>

      <div style={{ display: 'grid', gap: 12 }}>
        <input className="cpx-touch" style={input} value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder={kind === 'bug' ? 'What went wrong, in a line' : 'What would make this better, in a line'} />
        <textarea className="cpx-touch" style={{ ...input, minHeight: 86, resize: 'vertical' }}
          value={detail} onChange={(e) => setDetail(e.target.value)}
          placeholder={kind === 'bug' ? 'What you were doing, what you expected, what happened instead.' : 'As much or as little as you like. Paste a screenshot straight in.'} />
        <input className="cpx-touch" style={input} value={page} onChange={(e) => setPage(e.target.value)}
          placeholder="Which screen is it about? (Team board, Shipping Today, the packing slip...)" />
      </div>

      {files.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {files.map((f, i) => (
            <span key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 10px', background: '#F1F4F7', borderRadius: 8, fontSize: 12 }}>
              <Paperclip size={12} /> {f.filename}
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, color: 'var(--muted, #59707E)' }}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {err && <div style={{ marginTop: 12, fontSize: 13, color: '#8A3312' }}>{err}</div>}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
        <button type="submit" className="btn btn-primary" disabled={!title.trim() || busy} style={{ display: 'flex', alignItems: 'center', gap: 7, minHeight: 44 }}>
          <Send size={15} /> {busy ? 'Sending...' : 'Add it to the list'}
        </button>
        <button type="button" className="btn btn-outline" style={{ display: 'flex', alignItems: 'center', gap: 7, minHeight: 44 }}
          onClick={() => pick.current && pick.current.click()} disabled={busy || files.length >= 8}>
          <Paperclip size={15} /> Attach a screenshot
        </button>
        <input ref={pick} type="file" multiple accept="image/*,.pdf" style={{ display: 'none' }}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        <span style={{ fontSize: 12, color: 'var(--muted, #59707E)' }}>or paste one straight into the box</span>
      </div>
    </form>
  );
}

export default function WishList() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('open');

  const load = useCallback(async () => {
    try { setData(await post('wish-list', { action: 'list' })); setErr(null); }
    catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (payload) => {
    try { setData(await post('wish-list', payload)); setErr(null); }
    catch (e) { setErr(e.message); }
  };

  if (!data) return <div style={{ fontSize: 13.5, color: 'var(--muted, #59707E)' }}>{err || 'Loading the wish list...'}</div>;

  const me = data.me || {};
  const shown = data.wishes.filter(w => (filter === 'all' ? true : filter === 'done' ? w.status === 'done' : w.status !== 'done'));

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: '0 0 5px', fontSize: 13, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--navy)' }}>Wish list</h2>
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--muted, #59707E)', maxWidth: 720 }}>
          This is rough and there is a lot of polishing still to do. Rather than one long email thread,
          put it here: what is clunky, what is missing, what is plainly broken. Everyone can see everyone
          else&rsquo;s, so if two of you hit the same thing we will know.
        </p>
      </div>

      <Composer onSaved={load} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['open', 'Open'], ['done', 'Done'], ['all', 'Everything']].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} style={{
            minHeight: 36, padding: '0 14px', borderRadius: 999, cursor: 'pointer', fontSize: 13,
            fontWeight: filter === k ? 700 : 600, fontFamily: 'inherit',
            border: '1px solid ' + (filter === k ? 'var(--navy)' : 'var(--line, #D9E2E8)'),
            background: filter === k ? 'var(--navy)' : 'transparent',
            color: filter === k ? '#fff' : 'var(--muted, #59707E)',
          }}>{label}</button>
        ))}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: 'var(--muted, #59707E)', alignSelf: 'center' }}>
          {data.wishes.filter(w => w.status !== 'done').length} open
        </span>
      </div>

      {err && <div style={{ background: '#FDECE6', border: '1px solid #F4C3AE', borderRadius: 10, padding: '11px 15px', marginBottom: 16, fontSize: 13.5, color: '#8A3312' }}>{err}</div>}

      {shown.length === 0 ? (
        <div style={{ fontSize: 13.5, color: 'var(--muted, #59707E)' }}>Nothing here yet. Be the first.</div>
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          {shown.map(w => {
            const k = KIND[w.kind] || KIND.idea;
            const st = STATUS[w.status] || STATUS.new;
            const Icon = k.icon;
            const canEdit = w.author === me.key || me.steward;
            return (
              <div key={w.id} style={{ background: 'var(--surface, #fff)', border: '1px solid var(--line, #D9E2E8)', borderRadius: 14, padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <Icon size={17} color={k.color} style={{ marginTop: 2, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <h3 style={{ margin: 0, fontSize: 15.5, lineHeight: 1.35, fontWeight: 700 }}>{w.title}</h3>
                    {w.detail && <p style={{ margin: '7px 0 0', fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink, #0B1F2A)', whiteSpace: 'pre-wrap' }}>{w.detail}</p>}
                    <div style={{ marginTop: 9, fontSize: 12, color: 'var(--muted, #59707E)' }}>
                      {w.authorName} &middot; {w.createdOn}{w.page ? ' · ' + w.page : ''}
                    </div>
                    {w.files.length > 0 && (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                        {w.files.map(f => <Attachment key={f.id} file={f} />)}
                      </div>
                    )}
                    {w.statusNote && (
                      <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--navy)', background: '#F1F4F7', borderRadius: 8, padding: '8px 11px' }}>
                        {w.statusNote}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                    <span style={{ padding: '4px 11px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, background: st.bg, color: st.fg }}>{st.label}</span>
                    {me.steward && (
                      <select value={w.status} onChange={(e) => act({ action: 'status', id: w.id, status: e.target.value })}
                        style={{ fontSize: 12, padding: '5px 8px', borderRadius: 7, border: '1px solid var(--line, #D9E2E8)', fontFamily: 'inherit' }}>
                        {Object.entries(STATUS).map(([k2, s]) => <option key={k2} value={k2}>{s.label}</option>)}
                      </select>
                    )}
                    {me.steward && !w.projectId && (
                      <button onClick={() => act({ action: 'promote', id: w.id })}
                        style={{ border: 'none', background: 'none', color: 'var(--orange)', cursor: 'pointer', padding: 0, fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}>
                        Make it a project
                      </button>
                    )}
                    {canEdit && (
                      <button onClick={() => { if (window.confirm('Take this off the wish list?')) act({ action: 'remove', id: w.id }); }}
                        style={{ border: 'none', background: 'none', color: 'var(--muted, #59707E)', cursor: 'pointer', padding: 0, fontSize: 12, fontFamily: 'inherit' }}>
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
